import { Box, Divider, List, Typography } from '@mui/material';
import { Fragment } from 'react';
import { matchPath, useLocation } from 'react-router-dom';
import PropTypes from 'prop-types';

import useAuth from 'src/hooks/useAuth';
import { canAccessErrorMonitor } from 'src/utils/internalAccess';
import SidebarMenuItem from './item';
import menuItems from './items';

const absoluteLink = (link) => {
  if (!link || link.startsWith('/')) return link;
  return `/app/${link.replace(/^\/+/, '')}`;
};

const renderSidebarMenuItems = ({ items, path, expanded, user, depth = 0 }) => (
  <List component="div" disablePadding>
    {items.reduce((entries, item, index) => {
      if (item.roles && !item.roles.includes(user?.role)) return entries;
      if (item.errorMonitorAccess && !canAccessErrorMonitor(user)) return entries;
      const link = absoluteLink(item.link);
      const key = link || `${item.name || 'menu-item'}-${index}`;
      const exactMatch = link
        ? Boolean(matchPath({ path: link, end: true }, path))
        : false;
      const partialMatch = link
        ? Boolean(matchPath({ path: link, end: false }, path))
        : false;

      entries.push(
        <SidebarMenuItem
          key={key}
          active={item.items ? partialMatch : exactMatch}
          open={partialMatch}
          name={item.name}
          icon={item.icon}
          link={link}
          badge={item.badge}
          expanded={expanded}
          depth={depth}
        >
          {item.items ? renderSidebarMenuItems({
            path,
            items: item.items,
            expanded: true,
            user,
            depth: depth + 1
          }) : null}
        </SidebarMenuItem>
      );

      return entries;
    }, [])}
  </List>
);

function SidebarMenu({ expanded = false }) {
  const location = useLocation();
  const { user } = useAuth();

  return (
    <Box
      sx={{
        height: '100%',
        overflowY: 'auto',
        overflowX: 'hidden',
        px: expanded ? 0.75 : 0.5,
        py: 0.5,
        overscrollBehavior: 'contain'
      }}
    >
      {menuItems.map((section, index) => (
        <Fragment key={section.heading || section.items.map((item) => item.link || item.name).join('|')}>
          {expanded && section.heading ? (
            <Typography
              component="div"
              sx={{
                px: 1.25,
                pt: index ? 1.25 : 0.75,
                pb: 0.45,
                color: 'text.secondary',
                fontSize: '0.65rem',
                fontWeight: 800,
                textTransform: 'uppercase'
              }}
            >
              {section.heading}
            </Typography>
          ) : null}
          {renderSidebarMenuItems({
            items: section.items,
            path: location.pathname,
            expanded,
            user
          })}
          {index < menuItems.length - 1 ? (
            <Divider sx={{ mx: expanded ? 1.25 : 1.5, my: 0.75, opacity: 0.5 }} />
          ) : null}
        </Fragment>
      ))}
    </Box>
  );
}

SidebarMenu.propTypes = {
  expanded: PropTypes.bool
};

export default SidebarMenu;
